/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import JoiBrowser from 'joi-browser' // Npm: Joi for frontend validation.
import _ from 'underscore' // Npm: Underscore.js library.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useLazyQuery, useMutation } from '@apollo/client' // Npm: Apollo client.
import {
  Flex,
  useBreakpointValue
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import SubmitButton from 'components/SubmitButton'
import VendorAccounttDirectorySelector from 'components/VendorAccounttDirectorySelector'


/*
 * GRAPHS
 */
import LCRRoutePlanReadQuery from './__query__/index.lcrroutePlan.read.query'
import RoutePlanUpdateMutation from './__mutation__/index.lcrroutePlan.update.mutation'
import LCRRoutePlanCreateMutation from './__mutation__/index.lcrroutePlan.create.mutation'


/*
 * OBJECTS
 */
const Index = ({
  isOpen,
  isCreateOnly,
  onClose,
  passOn,
  refetch
}) => {
  // Hook assignment.
  const [error, setError] = React.useState('')
  const [formVersion, _ResetFormInputs] = React.useReducer(j => j + 1, 0)
  const [QueryLCRRoutePlanRead, QueryLCRRoutePlanReadResponse] = useLazyQuery(LCRRoutePlanReadQuery)
  const [LCRMutationRoutePlanCreate, LCRMutationRoutePlanCreateResponse] = useMutation(LCRRoutePlanCreateMutation)
  const [LCRMutationRoutePlanUpdate, LCRMutationRoutePlanUpdateResponse] = useMutation(RoutePlanUpdateMutation)
  const _formDataRef = React.useRef({})
  const _isCurrentViewMobile = useBreakpointValue({ 'base': false, 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Object assignment.
  const _SubmitForm = async e => {
    // Prevent default behavior.
    e.preventDefault()

    // Reset error.
    setError('')

    // Const assignment.
    const _JoiSchema = JoiBrowser.object({
      'vendorAccounttDirectory': JoiBrowser.string().required()
    }).options({ 'allowUnknown': true })

    // Report failure if vendorAccounttDirectory is not set.
    if (_.isEmpty(_formDataRef.current?.vendorAccounttDirectory)) return setError('vendorAccounttDirectory is required.')


    // Remove all keys from _formDataRef.current which are undefined.
    _formDataRef.current = { ..._.pick(_formDataRef.current, _.identity) }

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_formDataRef.current)

    // If error exists then report failure.
    if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)

    /*
     * Update _formDataRef with routePlanId
     * if it doesn't exist and also make sure that mode
     * is not create only.
     */
    if (!isCreateOnly && _.isEmpty(_formDataRef.current?.routePlanId)) _formDataRef.current = { ..._formDataRef.current, 'routePlanId': passOn?.routePlanId }
    if (isCreateOnly && _.isEmpty(_formDataRef.current?.routeId)) _formDataRef.current = { ..._formDataRef.current, 'routeId': passOn?.routeId }

    /*
     * Update _formDataRef with input and routePlan
     * if it doesn't exist. Also make sure that mode is
     * not create only.
     */
    if (_.isEmpty(_formDataRef.current?.input)) _formDataRef.current = ({ 'input': [_formDataRef.current], ..._formDataRef.current })

    // Execute update mutation.
    const _LCRMutationRoutePlanUpdate = await [isCreateOnly ? LCRMutationRoutePlanCreate : LCRMutationRoutePlanUpdate]?.[0]({
      'variables': {
        'routePlanId': _.isEmpty(passOn?.routePlanIds) ? isCreateOnly ? '' : passOn?.routePlanId : void 0,
        'routePlanIds': _.isEmpty(passOn?.routePlanIds) ? void 0 : isCreateOnly ? '' : passOn?.routePlanIds,
        'input': _.pick(_formDataRef.current, 'input').input.map(j => {
          // Const assignment.
          let _vendorAccountt, _vendorAccounttDirectory

          // Variable assignment.
          _vendorAccountt = j?.vendorAccountt
          _vendorAccounttDirectory = j?.vendorAccounttDirectory

          // Conditionally update vendorAccountt and vendorAccounttDirectory.
          if (j?.vendorAccountt && 0 < j?.vendorAccountt?.length) _vendorAccountt = _.compact(_.flatten(j?.vendorAccountt.map(r => r?.includes('(') && r?.includes(')') ? r.split('(')[1].split(')')[0] : r)))
          if (j?.vendorAccounttDirectory && (j?.vendorAccounttDirectory?.includes('(') || j?.vendorAccounttDirectory?.includes(')'))) _vendorAccounttDirectory = j?.vendorAccounttDirectory.split('(')[1].split(')')[0]

          // Return data.
          return ({
            'vendorAccountt': _vendorAccountt,
            'vendorAccounttDirectory': _vendorAccounttDirectory,
            ...(isCreateOnly ? { 'routeId': passOn?.routeId } : {})
          })
        })
      }
    })

    // If mutation caught an exception then report failure.
    if (_LCRMutationRoutePlanUpdate instanceof Error) return _LCRMutationRoutePlanUpdate

    // Check if create was successful.
    if (!_.isEmpty(_LCRMutationRoutePlanUpdate?.data?.LCRRoutePlanCreate) && _.every(_.pluck(_LCRMutationRoutePlanUpdate?.data?.LCRRoutePlanCreate, 'status') ?? [], j => 'CREATE_SUCCESSFUL' === j)) {
      // Style guide.
      toast('Successfully upserted the route plan.')

      // Execute refetch.
      refetch?.()

      // On successful create close modal.
      return onClose?.()
    }
    if (!_.isEmpty(_LCRMutationRoutePlanUpdate?.data?.LCRRoutePlanUpdate) && _.every(_.pluck(_LCRMutationRoutePlanUpdate?.data?.LCRRoutePlanUpdate, 'status') ?? [], j => 'UPDATE_SUCCESSFUL' === j)) {
      // Style guide.
      toast('Successfully upserted the route plan.')

      // Execute refetch.
      refetch?.()

      // Report failure.
      return onClose?.()
    }

    // Style Guide.
    toast((_.isEmpty(_LCRMutationRoutePlanUpdate?.data?.LCRRoutePlanCreate) ? [0 === _.pluck(_LCRMutationRoutePlanUpdate?.data?.LCRRoutePlanUpdate, 'message')?.length ? 'Same plan already exists. kindly delete it or modify it instead.' : _.pluck(_LCRMutationRoutePlanUpdate?.data?.LCRRoutePlanUpdate, 'message')]?.join(' ') : _.pluck(_LCRMutationRoutePlanUpdate?.data?.LCRRoutePlanCreate, 'message')?.join(' ')))

    // Report void.
    return void 0
  }

  // Event handler.
  React.useEffect(() => {
    // Only prepare the form while the modal is open.
    if (!isOpen) return

    // Create mode should reset local form state without hitting read query.
    if (isCreateOnly) {
      _formDataRef.current = {}
      setError('')
      _ResetFormInputs()
      return
    }

    if (_.isEmpty(passOn?.routePlanId)) return

    let _isActive = true

    // _Async handler.
    const _Async = async () => {
      // Const assignment.
      const _QueryLCRRoutePlanReadQuery = await QueryLCRRoutePlanRead({ 'variables': { 'routePlanId': passOn?.routePlanId } })

      if (!_isActive) return void 0

      // If query caught an exception then report failure.
      if (_QueryLCRRoutePlanReadQuery instanceof Error) return _QueryLCRRoutePlanReadQuery

      /*
       * If routePlan details fetch complete then
       * update its value.
       */
      if (_QueryLCRRoutePlanReadQuery?.data?.LCRRoutePlanRead) {
        // Const assignment.
        const _routePlanRead = _.first(_QueryLCRRoutePlanReadQuery?.data?.LCRRoutePlanRead)

        // Update form data.
        _formDataRef.current = {
          'routePlanId': passOn?.routePlanId,
          'createdAt': _routePlanRead?.createdAt,
          'updatedAt': _routePlanRead?.updatedAt,
          'vendorAccountt': _routePlanRead?.VendorAccountt?.map(i => `${i.displayName} (${i.id})`),
          'vendorAccounttDirectory': _routePlanRead?.VendorAccounttDirectory?.displayName ? `${_routePlanRead?.VendorAccounttDirectory?.displayName} (${_routePlanRead?.VendorAccounttDirectory?.id})` : void 0
        }

        // Reset uncontrolled inputs once loaded data is ready.
        return _ResetFormInputs()
      }

      // Report failure.
      return void 0
    }; _Async()

    return () => {
      _isActive = false
    }
  }, [QueryLCRRoutePlanRead, _ResetFormInputs, isCreateOnly, isOpen, passOn?.routePlanId])

  // Const assignment.
  const _isLoading = LCRMutationRoutePlanUpdateResponse.loading || LCRMutationRoutePlanCreateResponse.loading
  const _isInputDisabled = isCreateOnly ? false : _isLoading || QueryLCRRoutePlanReadResponse.loading

  // Return component.
  return (
    <form onSubmit={_SubmitForm} className='routePlanUpsert' key={formVersion}>
      <Flex gap='22px' flexDir='column' wrap='nowrap' w='100%'>

        <Flex width='100%' gap='18px' direction={{ 'base': 'column', 'md': 'row' }}>
          <VendorAccounttDirectorySelector
            disabled={_isInputDisabled}
            bg='gray.300'
            isRequired={true}
            vendorAccounttDirectoryValue={_formDataRef?.current?.vendorAccounttDirectory ?? ''}
            vendorAccounttValue={_formDataRef?.current?.vendorAccountt}
            inValidVendorAccountt={error?.includes('vendorAccountt')}
            inValidVendorAccounttDirectory={error?.includes('vendorAccounttDirectory')}
            onChange={i => {
              // Update form data.
              _formDataRef.current = ({ ..._formDataRef.current, 'vendorAccountt': i.vendorAccountt, 'vendorAccounttDirectory': _.isEmpty(i.vendorAccounttDirectory) ? void 0 : i.vendorAccounttDirectory })
            }}
          />
        </Flex>
      </Flex>

      {/* Submit Button */}
      <Flex
        mt='22px'
        alignItems={_isCurrentViewMobile ? 'flex-end' : 'center'}
        justifyContent='flex-end'
        flexDir='row'>
        <SubmitButton
          mt={0}
          disabled={_isInputDisabled}
          onSubmit={_SubmitForm}
          defaultText={isCreateOnly ? 'Create LCR RoutePlan' : 'Update LCR RoutePlan'}
          isLoading={_isLoading} />
      </Flex>
    </form>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'isCreateOnly': PropTypes.bool,
  'isOpen': PropTypes.bool,
  'onClose': PropTypes.func,
  'passOn': PropTypes.object,
  'submitRef': PropTypes.object,
  'disabled': PropTypes.bool,
  'isUpsertMixerContext': PropTypes.bool,
  'setSubmitIsLoading': PropTypes.func,
  'refetch': PropTypes.func
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(React.memo(Index))
